import { useState, useEffect } from 'react';
import {
    collection,
    query,
    where,
    onSnapshot,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    orderBy,
    getDocs,
    arrayUnion,
    arrayRemove,
    increment,
    writeBatch,
    deleteField
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

export const usePlaybooks = () => {
    const { currentUser } = useAuth();
    const [playbooks, setPlaybooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [activePlaybook, setActivePlaybook] = useState(null);

    useEffect(() => {
        if (!currentUser) {
            setPlaybooks([]);
            setLoading(false);
            return;
        }

        // Query playbooks where user is owner OR a collaborator
        const q = query(
            collection(db, 'playbooks'),
            where('collaboratorIds', 'array-contains', currentUser.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const pbList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Sort by updatedAt descending in memory
            pbList.sort((a, b) => {
                const dateA = a.updatedAt?.toDate ? a.updatedAt.toDate() : (a.updatedAt || 0);
                const dateB = b.updatedAt?.toDate ? b.updatedAt.toDate() : (b.updatedAt || 0);
                return dateB - dateA;
            });

            setPlaybooks(pbList);
            setLoading(false);
            setError(null);
        }, (err) => {
            console.error("Error fetching playbooks:", err);
            setError(err);
            setLoading(false);
            if (err.code === 'failed-precondition') {
                alert("Firestore Index Required: " + err.message);
            }
        });

        return unsubscribe;
    }, [currentUser]);

    // Optional: Fetch a specific playbook if we have an ID but it's not in the list
    // This handles public shares for non-logged in users
    const fetchPlaybookById = async (id) => {
        if (!id) return null;
        const existing = playbooks.find(p => p.id === id);
        if (existing) return existing;

        try {
            const pbSnap = await getDocs(query(collection(db, 'playbooks'), where('__name__', '==', id)));
            if (!pbSnap.empty) {
                return { id: pbSnap.docs[0].id, ...pbSnap.docs[0].data() };
            }
        } catch (err) {
            console.error("Error fetching single playbook:", err);
        }
        return null;
    };

    const createPlaybook = async (name, profileData = {}) => {
        if (!currentUser) return;
        return await addDoc(collection(db, 'playbooks'), {
            name,
            ...profileData, // { profile: {...}, labelMode: '...' }
            ownerId: currentUser.uid,
            collaboratorIds: [currentUser.uid],
            collaborators: {
                [currentUser.uid]: {
                    role: 'owner',
                    email: currentUser.email,
                    displayName: currentUser.displayName
                }
            },
            preventDuplication: false, // Default setting
            playCount: 0,
            notes: "",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
    };

    const duplicatePlaybook = async (pbId) => {
        if (!currentUser) return;
        const sourcePb = playbooks.find(p => p.id === pbId);
        if (!sourcePb) return;

        // 1. Get all plays first
        const playsRef = collection(db, 'playbooks', pbId, 'plays');
        const playsSnap = await getDocs(playsRef);
        const sourcePlays = playsSnap.docs;

        // 2. Prepare new playbook data
        const newPbRef = doc(collection(db, 'playbooks'));
        const newPbData = {
            ...sourcePb,
            name: `${sourcePb.name} (Copy)`,
            ownerId: currentUser.uid,
            collaboratorIds: [currentUser.uid],
            collaborators: {
                [currentUser.uid]: {
                    role: 'owner',
                    email: currentUser.email,
                    displayName: currentUser.displayName
                }
            },
            id: null,
            playCount: sourcePlays.length, // accurate count
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        };

        // 3. Batch operations (limit 500 per batch)
        const BATCH_SIZE = 450; // Safety margin
        const chunks = [];
        let currentBatch = writeBatch(db);
        let opCount = 0;

        // Add Playbook Create to first batch
        currentBatch.set(newPbRef, newPbData);
        opCount++;

        // Add Plays to batches
        for (const playDoc of sourcePlays) {
            if (opCount >= BATCH_SIZE) {
                chunks.push(currentBatch.commit());
                currentBatch = writeBatch(db);
                opCount = 0;
            }

            const playData = playDoc.data();
            const newPlayRef = doc(collection(db, 'playbooks', newPbRef.id, 'plays'));
            currentBatch.set(newPlayRef, {
                ...playData,
                id: newPlayRef.id,
                playbookId: newPbRef.id,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
            opCount++;
        }

        // Push final batch
        if (opCount > 0) chunks.push(currentBatch.commit());

        // Execute all batches
        await Promise.all(chunks);
        return newPbRef.id;
    };

    const updatePlaybook = async (id, data) => {
        const pbRef = doc(db, 'playbooks', id);
        return await updateDoc(pbRef, {
            ...data,
            updatedAt: serverTimestamp()
        });
    };

    const deletePlaybook = async (id) => {
        if (!currentUser) return;

        // 1. Get all plays
        const playsRef = collection(db, 'playbooks', id, 'plays');
        const playsSnap = await getDocs(playsRef);

        // 2. Batch Delete
        const BATCH_SIZE = 450;
        const chunks = [];
        let curBatch = writeBatch(db);
        let opCount = 0;

        // Add plays to delete
        playsSnap.docs.forEach(doc => {
            if (opCount >= BATCH_SIZE) {
                chunks.push(curBatch.commit());
                curBatch = writeBatch(db);
                opCount = 0;
            }
            curBatch.delete(doc.ref);
            opCount++;
        });

        // Add parent playbook to delete
        // Included in the final batch to ensure (mostly) atomic removal
        const pbRef = doc(db, 'playbooks', id);
        curBatch.delete(pbRef);
        chunks.push(curBatch.commit());

        await Promise.all(chunks);
    };

    const inviteUserByEmail = async (playbookId, email) => {
        try {
            const { httpsCallable } = await import('firebase/functions');
            const { functions } = await import('../firebase');
            const searchFunc = httpsCallable(functions, 'searchUserByEmail');
            const result = await searchFunc({ email });

            if (!result.data.found) {
                throw new Error("No user found with that email. Tell them to sign up first!");
            }

            const targetUser = result.data.user;
            const pbRef = doc(db, 'playbooks', playbookId);

            // Update playbook with new collaborator
            await updateDoc(pbRef, {
                collaboratorIds: arrayUnion(targetUser.uid),
                [`collaborators.${targetUser.uid}`]: {
                    role: 'editor',
                    email: targetUser.email,
                    displayName: targetUser.displayName
                },
                updatedAt: serverTimestamp()
            });
        } catch (err) {
            console.error("Invite Error:", err);
            throw err;
        }
    };

    const removeCollaborator = async (playbookId, userId) => {
        const pbRef = doc(db, 'playbooks', playbookId);
        await updateDoc(pbRef, {
            collaboratorIds: arrayRemove(userId),
            [`collaborators.${userId}`]: deleteField(),
            updatedAt: serverTimestamp()
        });
    };

    const setPlaybookPublic = async (id, isPublic) => {
        const pbRef = doc(db, 'playbooks', id);
        return await updateDoc(pbRef, {
            isPublic,
            updatedAt: serverTimestamp()
        });
    };

    return {
        playbooks,
        loading,
        error,
        createPlaybook,
        duplicatePlaybook,
        updatePlaybook,
        deletePlaybook,
        inviteUserByEmail,
        removeCollaborator,
        setPlaybookPublic,
        fetchPlaybookById
    };
};
